import moment from "moment";
import "moment/locale/fr";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { isDepositEnabled } from "../../config/environment.config";
import { isVerifiedTrainer, vtCoursePrixTotal, vtCourseRemuneration } from "../../Utils/VerifiedTrainerAmounts";
import { clock, dayLabel, euros, hoursFromHalfHours, monthName, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { isRelationTeachrCourse } from "../DeclarationV2/components/relationTeachr";

/**
 * Lecture d'une ligne de cours de l'API du tableau de bord (format
 * `_punctualCourseToDictForDashboard`) pour le tableau « Mes cours » repensé :
 * libellés des dates, matière, montants et acompte. Aucune règle métier
 * nouvelle, uniquement de la mise en forme.
 */

export interface CourseSession {
    date: moment.Moment;
    minutes: number;
}

/**
 * Ancien cours « Plusieurs cours » sans détail des séances : déclaré avant les
 * séances détaillées (25 août 2026) ou sans elles, il n'a en base qu'une
 * séance de reprise, placée au premier jour avec toute la durée du mois,
 * alors que sa période court sur plusieurs jours. Les vrais jours n'ont
 * jamais été enregistrés : on montre la période du cours, pas cette séance.
 */
export const isUndetailedMonthCourse = (c: any): boolean => {
    if (!c?.isPunctualMonth) return false;
    const count = Array.isArray(c?.sessions) ? c.sessions.length : 0;
    if (count > 1) return false;
    const start = moment(c?.startDatetime);
    const end = moment(c?.endDatetime);
    return start.isValid() && end.isValid() && !start.isSame(end, "day");
};

/** « Séances non détaillées, 20 h au total » */
export const undetailedSessionsLabel = (c: any): string => `Séances non détaillées, ${hoursFromHalfHours(Number(c?.duration ?? 0))} au total`;

/**
 * Séances du cours : celles de l'API pour un cours mensuel, sinon le cours
 * lui-même. Aucune pour un ancien cours mensuel sans détail des séances.
 */
export const courseSessions = (c: any): CourseSession[] => {
    if (isUndetailedMonthCourse(c)) return [];
    if (c?.isPunctualMonth && Array.isArray(c?.sessions) && c.sessions.length > 0) {
        return c.sessions
            .map((s: any) => ({ date: moment(s?.date), minutes: Number(s?.durationMinutes ?? 0) }))
            .filter((s: CourseSession) => s.date.isValid())
            .sort((a: CourseSession, b: CourseSession) => a.date.valueOf() - b.date.valueOf());
    }
    return [{ date: moment(c?.startDatetime), minutes: Number(c?.duration ?? 0) * 30 }];
};

export const courseRange = (c: any): { start: moment.Moment; end: moment.Moment } => {
    const sessions = courseSessions(c);
    if (c?.isPunctualMonth && sessions.length > 0) {
        return { start: sessions[0].date, end: sessions[sessions.length - 1].date };
    }
    return { start: moment(c?.startDatetime), end: moment(c?.endDatetime) };
};

/** « 1er », « 15 » */
const dayNumber = (d: moment.Moment): string => (d.date() === 1 ? "1er" : String(d.date()));

/** « Jeudi 15 octobre », « Du 2 au 31 octobre », « Du 24 octobre au 1er novembre », « 30 et 31 octobre ». */
export const rangeLabel = (start: moment.Moment, end: moment.Moment, dates?: moment.Moment[]): string => {
    if (start.isSame(end, "day")) return dayLabel(start, !start.isSame(moment(), "year"));
    const sameMonth = start.isSame(end, "month");
    if (dates && dates.length === 2 && sameMonth) return `${dayNumber(start)} et ${dayNumber(end)} ${monthName(end)}`;
    if (sameMonth) return `Du ${dayNumber(start)} au ${dayNumber(end)} ${monthName(end)}`;
    const year = start.isSame(end, "year") ? "" : ` ${end.format("YYYY")}`;
    return `Du ${dayNumber(start)} ${monthName(start)} au ${dayNumber(end)} ${monthName(end)}${year}`;
};

/** « Jeudi 15 octobre » ou la plage des séances, pour la première ligne de la colonne Dates. */
export const courseDatesLabel = (c: any): string => {
    const { start, end } = courseRange(c);
    const sessions = courseSessions(c);
    return rangeLabel(start, end, c?.isPunctualMonth ? sessions.map((s) => s.date) : undefined);
};

/** « samedi 31 à 10h », « vendredi 6 à 17h30 » */
const sessionShortLabel = (d: moment.Moment): string => `${d.locale("fr").format("dddd D")} à ${d.minute() === 0 ? d.format("H[h]") : d.format("H[h]mm")}`;

/**
 * Seconde ligne de la colonne Dates : l'horaire d'un cours unique, ou le
 * nombre de séances suivi de la prochaine (cours à venir) quand elle existe.
 */
export const courseDatesSubline = (c: any, withNext: boolean): string => {
    if (!c?.isPunctualMonth) {
        return `${clock(c?.startDatetime)} à ${clock(c?.endDatetime)}`;
    }
    if (isUndetailedMonthCourse(c)) return undetailedSessionsLabel(c);
    const sessions = courseSessions(c);
    const count = plural(sessions.length, "séance");
    if (!withNext || sessions.length === 0) return count;
    const now = moment();
    const next = sessions.find((s) => s.date.isSameOrAfter(now, "minute"));
    if (!next) return count;
    const isFirst = next === sessions[0];
    return `${count}, ${isFirst ? "première" : "prochaine"} ${sessionShortLabel(next.date)}`;
};

/** « Anglais, plusieurs cours » / « Soutien, un seul cours » */
export const courseSubjectLine = (c: any): string => {
    const subject = capitalizeFirstLetter(c?.subjectAsked ?? "") || vocab.subjectLabel();
    const format = c?.isPunctualMonth ? vocab.multipleCoursesTitle() : vocab.singleCourseTitle();
    return `${subject}, ${format.charAt(0).toLowerCase()}${format.slice(1)}`;
};

/** Format seul : « Plusieurs cours » / « Un seul cours » */
export const courseFormatLabel = (c: any): string => (c?.isPunctualMonth ? vocab.multipleCoursesTitle() : vocab.singleCourseTitle());

/**
 * Ce que le prof reçoit pour le cours : la rémunération enregistrée à la déclaration (part HT + TVA, `teachrVat`),
 * telle que sa facture la porte, pour toutes les marques. Chez Teach'r un prof hors coopérative y retrouve
 * teachr_price × heures (sa part est TTC), et un prof passé par la coopérative Verified Trainer SAP sa part
 * du HT, sans TVA s'il n'est pas assujetti. Repli sur teachr_price × heures pour un cours antérieur à la migration.
 */
export const courseReceived = (c: any): number => vtCourseRemuneration(c);

/** Verified Trainer seulement : prix total TTC enregistré du cours. */
export const courseTtc = (c: any): number | null => (isVerifiedTrainer() ? vtCoursePrixTotal(c) : null);

/**
 * Montant mis en avant dans les listes, comme dans la liste actuelle : le prix
 * total TTC chez Verified Trainer, la rémunération chez Teach'r et Déclarons.
 * Client en relation Teach'r : toujours la rémunération, le prix total
 * comprend la part de la plateforme que le prof ne doit pas voir.
 */
export const courseAmount = (c: any): number => (isVerifiedTrainer() && !isRelationTeachrCourse(c) ? vtCoursePrixTotal(c) : courseReceived(c));

/** Verified Trainer : la rémunération, rappelée sous le prix total (pas de rappel pour une relation Teach'r). */
export const courseAmountDetail = (c: any): string | null =>
    (isVerifiedTrainer() && !isRelationTeachrCourse(c) ? `rémunération ${euros(vtCourseRemuneration(c))}` : null);

/** Libellé de la colonne des montants : « Prix total » chez Verified Trainer, « Vous recevez » ailleurs. */
export const amountColumnLabel = (): string => (isVerifiedTrainer() ? "Prix total" : "Vous recevez");

/** Acompte déjà reçu, quand la marque le permet et qu'il existe. */
export const courseDeposit = (c: any): number | null => {
    if (!isDepositEnabled()) return null;
    const value = Number(c?.deposit ?? 0);
    return value > 0 ? value : null;
};

/** Nom affiché : l'élève, à défaut le titulaire du compte. */
export const courseStudentName = (row: any): string => {
    const child = [row?.forChildFirstName, row?.forChildLastName].filter(Boolean).map((s: string) => capitalizeFirstLetter(s)).join(" ");
    const parent = [row?.firstName, row?.lastName].filter(Boolean).map((s: string) => capitalizeFirstLetter(s)).join(" ");
    return child || parent || "-";
};

export const courseParentName = (row: any): string | null => {
    const child = [row?.forChildFirstName, row?.forChildLastName].filter(Boolean).join(" ").toLowerCase();
    const parent = [row?.firstName, row?.lastName].filter(Boolean).map((s: string) => capitalizeFirstLetter(s)).join(" ");
    return parent && parent.toLowerCase() !== child ? parent : null;
};

export const courseInitials = (row: any): string =>
    `${(row?.forChildFirstName ?? row?.firstName ?? "").charAt(0)}${(row?.forChildLastName ?? row?.lastName ?? "").charAt(0)}`.toUpperCase() || "?";

// ─── Groupes par élève (onglet Ce mois) ──────────────────────────────────────

export const groupRows = (group: any): any[] => (Array.isArray(group?.courses) ? group.courses : []);

export const groupSessionCount = (group: any): number => groupRows(group).reduce((n, c) => n + courseSessions(c).length, 0);

/** Durée totale en demi-heures, comme l'API. */
export const groupHalfHours = (group: any): number => groupRows(group).reduce((n, c) => n + (Number(c?.duration ?? 0) || 0), 0);

export const groupReceived = (group: any): number => groupRows(group).reduce((n, c) => n + courseReceived(c), 0);

/** Montant mis en avant du groupe (prix total TTC chez Verified Trainer, rémunération ailleurs). */
export const groupAmount = (group: any): number => groupRows(group).reduce((n, c) => n + courseAmount(c), 0);

/** Verified Trainer : rémunération du groupe, rappelée sous le prix total (sauf si tous les cours sont en relation Teach'r). */
export const groupAmountDetail = (group: any): string | null =>
    (isVerifiedTrainer() && !groupRows(group).every(isRelationTeachrCourse) ? `rémunération ${euros(groupReceived(group))}` : null);

export const groupDeposit = (group: any): number | null => {
    const total = groupRows(group).reduce((n, c) => n + (courseDeposit(c) ?? 0), 0);
    return total > 0 ? total : null;
};

export const groupRange = (group: any): { start: moment.Moment; end: moment.Moment } => {
    const ranges = groupRows(group).map(courseRange);
    if (ranges.length === 0) return { start: moment(group?.dates?.startDatetime), end: moment(group?.dates?.endDatetime) };
    return { start: moment.min(ranges.map((r) => r.start)), end: moment.max(ranges.map((r) => r.end)) };
};

/** Ligne enrichie des identifiants du groupe, attendue par le menu d'actions. */
export const withGroupIds = (c: any, group: any) => ({
    ...c,
    forChild_id: group?.forChild_id,
    clientId: group?.clientId,
    startAt: group?.startAt,
    firstName: group?.firstName,
    lastName: group?.lastName,
    forChildFirstName: c?.forChildFirstName ?? group?.forChildFirstName,
    forChildLastName: c?.forChildLastName ?? group?.forChildLastName,
});
